import React, { useEffect, useRef, useState } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import { getError } from 'utils/appHelpers';
import { API, graphqlOperation } from 'aws-amplify';
import { searchLikieesQuery } from 'graphql/likiees/queries';
import { addLikieesToProjects } from 'graphql/projects/mutations';
import MasonryInfiniteScroller from 'react-masonry-infinite';
import Modal from '../modals/Modal';
import ProductBlock from './ProductBlock';
import Loading from '../Loading';
import useScrollEnd from 'shared/hooks/useScrollEnd';
import Button from '../Button';

const SelectAndAddProductToProject = ({ onClose, onSuccess, projectID }) => {
  const [fetching, setFetching] = useState(false);
  const alert = useAlert();
  const masonryEl = useRef();
  const container = useRef();
  const loadState = useRef({ canLoad: false });
  const [products, setProducts] = useState({ items: [] });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const { t } = useTranslation();

  const onSubmitProducts = async () => {
    try {
      setFetching(true);
      const body = { projectIDs: [projectID], likieeIDs: [...selectedProducts] };
      await API.graphql(graphqlOperation(addLikieesToProjects, body));
      setFetching(false);
      alert.success(t('msg_success_product_project_updated'));
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      alert.error(getError(err));
      setFetching(false);
    }
  };

  const onSelectProduct = item => {
    const tempProducts = [...selectedProducts];
    if (tempProducts.includes(item.id)) {
      tempProducts.splice(tempProducts.indexOf(item.id), 1);
    } else {
      tempProducts.push(item.id);
    }
    setSelectedProducts(tempProducts);
  };

  const getProductsData = async nextToken => {
    try {
      loadState.current.canLoad = false;
      const body = { limit: 20, nextToken, sort: { direction: 'desc', field: 'createTime' } };
      const { data } = await API.graphql(graphqlOperation(searchLikieesQuery, body));
      setProducts(products => {
        const merged = { ...data.searchLikiees, items: [...products.items, ...data.searchLikiees.items] };
        loadState.current.canLoad = merged.items.length < merged.total;
        loadState.current.token = merged.nextToken;
        return merged;
      });
      setTimeout(masonryEl.current.forcePack, 1000);
      setTimeout(masonryEl.current.forceUpdate, 1000);
    } catch (err) {
      alert.error(getError(err));
    }
  };

  useScrollEnd(container, loadState, token => {
    getProductsData(token);
  });

  useEffect(() => {
    getProductsData();
  }, []);

  return (
    <Modal
      isOpen={true}
      className={`manage-modal d-flex flex-column mx-8 py-15 py-md-30 px-8 px-md-58 is-large add-product-from-list overflow-hidden`}
      onCloseModal={onClose}
    >
      <h1 className='mb-10 mb-md-10'>{t('title_add_product_modal')}</h1>
      <div className='max-h-600 min-h-600 overflow-auto position-static' ref={container}>
        {!!products?.items?.length && (
          <MasonryInfiniteScroller
            ref={masonryEl}
            pack={true}
            hasMore={products?.items?.length < products?.total}
            loadMore={getProductsData.bind(null, products.nextToken)}
            sizes={[
              { columns: 1, gutter: 10 },
              { mq: '768px', columns: 3, gutter: 20 },
              { mq: '1024px', columns: 4, gutter: 32 },
            ]}
          >
            {products?.items?.map((item, i) => {
              const isSelected = selectedProducts.includes(item.id);
              return (
                <ProductBlock
                  product={item}
                  key={i}
                  showAlarm={false}
                  showDate={true}
                  notAvailable={item.availability === 'OutOfStock'}
                  hideProductClick={true}
                  onSelectToggle={onSelectProduct.bind(null, item)}
                  selected={!!isSelected}
                  isCheckbox={true}
                />
              );
            })}
          </MasonryInfiniteScroller>
        )}
        {!products?.items?.length && <Loading />}
      </div>
      <div className='products-organize py-15 b-0 bg-white l-0 position-absolute z-index-100 w-100 shadow'>
        <div className='container d-flex align-items-center justify-content-end'>
          <p className='mb-0'>{t('selected_products', { count: selectedProducts.length })}</p>
          <Button disabled={fetching} className='btn-primary ml-16 btn-sm' onClick={onSubmitProducts}>
            {t('btn_add')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SelectAndAddProductToProject;
